import React from 'react';
import { PageHeader } from '../components/Shared/PageHeader';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  useFetchQuestionnaireListQuery,
  useUpdateQuestionnaireStatusMutation,
} from '../redux/api/questionnaireApi';
import { IQuestionnaire } from '../common/interfaces/Questionnaire.interface';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { TableCell, TableCommon } from '../components/Table/Table';
import { Button, Table, ToggleSwitch, Tooltip } from 'flowbite-react';
import { getLocalizedValueFromMultiLangValues } from '../utils/getLocalizedValueFromMultiLangValues';
import { QuestionnaireModal } from '../components/QuestionnaireModal/QuestionnaireModal';
import { Status } from '../common/enums/Status';
import { QuestionnaireCreateModal } from '../components/QuestionnaireModal/QuestionnaireCreateModal';
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';
import { usePagination } from '../hooks/usePagination';
import { paginate } from '../utils/paginate';

export const QuestionnairePage: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [item, setItem] = React.useState<IQuestionnaire | null>(null);
  const [questionnaireData, setQuestionnaireData] = React.useState<
    IQuestionnaire[]
  >([]);

  const { t } = useTranslation();
  usePageTitle(t('pageTitles.questionnaire'));

  const handleCloseModal = () => {
    setIsOpen(false);
    setItem(null);
  };

  const handleOpenModal = (item: IQuestionnaire) => {
    setItem(item);
    setIsOpen(true);
  };

  const {
    data: response,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useFetchQuestionnaireListQuery();

  React.useEffect(() => {
    if (response?.data) {
      setQuestionnaireData(response.data);
    }
  }, [response]);

  const [updateQuestionnaireStatus] = useUpdateQuestionnaireStatusMutation();

  // The endpoint returns every questionnaire; page the (small) list here. The page lives in the URL,
  // so toggling a status keeps the person on the same page.
  const { currentPage, setCurrentPage, size } = usePagination(10);
  const questionnaires = paginate(questionnaireData, currentPage, size);

  const handleStatusChange = async (id: string, newStatus: Status) => {
    try {
      const response = await updateQuestionnaireStatus({
        id,
        data: { status: newStatus },
      });

      if (response?.data) {
        const updatedQuestionnaireData = questionnaireData.map((item) =>
          item.id === id ? { ...item, status: newStatus } : item
        );
        setQuestionnaireData(updatedQuestionnaireData);
      }
    } catch (error) {
      console.error('Failed to update questionnaire status', error);
    }
  };

  const columnHeadLabels = [
    { id: 'body', label: t('questionnaireLabels.body') },
    { id: 'status', label: t('questionnaireLabels.status') },
    { id: 'questions', label: t('questionnaireLabels.questions') },
    { id: 'show', label: t('questionnaireLabels.show') },
  ];

  if (isLoading) return <Loading />;

  if (isError) return <ErrorComponent error={loadError} onRetry={refetch} />;

  return (
    <div className="p-6">
      <PageHeader
        title={t('content.questionnaire')}
        buttonText={`+ ${t('questionnaireLabels.addNew')}`}
        buttonOnClick={() => setIsCreateOpen(true)}
      />
      <TableCommon columns={columnHeadLabels}>
        {questionnaires.pageItems.map((questionnaireData) => (
          <Table.Row key={questionnaireData.id} className="bg-white">
            <TableCell>
              {getLocalizedValueFromMultiLangValues(
                questionnaireData.body || '--'
              )}
            </TableCell>
            <TableCell>
              <div className="flex items-center justify-center">
                <Tooltip
                  content={
                    questionnaireData.status === Status.ACTIVE
                      ? t('status.ACTIVE')
                      : t('status.INACTIVE')
                  }
                  placement="top"
                >
                  <ToggleSwitch
                    checked={questionnaireData.status === Status.ACTIVE}
                    onChange={() =>
                      handleStatusChange(
                        questionnaireData.id,
                        questionnaireData.status === Status.ACTIVE
                          ? Status.INACTIVE
                          : Status.ACTIVE
                      )
                    }
                  />
                </Tooltip>
              </div>
            </TableCell>
            <TableCell>{questionnaireData.questions.length}</TableCell>
            <TableCell>
              <Button
                size="xs"
                className="btn-light mx-auto"
                disabled={questionnaireData.questions.length < 1}
                onClick={() => handleOpenModal(questionnaireData)}
              >
                {t('questionnaireLabels.show')}
              </Button>
            </TableCell>
          </Table.Row>
        ))}
      </TableCommon>
      <CustomPagination
        currentPage={questionnaires.currentPage}
        onPageChange={setCurrentPage}
        totalPages={questionnaires.totalPages}
      />
      <QuestionnaireModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        item={item}
      />
      <QuestionnaireCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
    </div>
  );
};
